import React, { Component} from 'react';
import ChatBot from 'react-simple-chatbot';
import { ThemeProvider } from 'styled-components';
import { Accordion, Card, Collapse, Image, Spinner } from 'react-bootstrap';
import './bootstrap.min.css';
import { Analytics } from '@vercel/analytics/react';
import './App.css';
import TravelPlanMap from './TravelPlanMap';
import ShareModal from './ShareModal';
import MailingListModal from './MailingListModal';
import SampleItineraries from './SampleItineraries';

let hostname = 'http://localhost:3001'; // node run server
// const hostname = ''; // vercel dev
let debug = true;

if (window.location.hostname !== 'localhost') {
  hostname = 'https://tripgpt.herokuapp.com'; // heroku
  debug = false;
}

const decoder = new TextDecoder();

const theme = {
  background: '#9493D3',
  fontFamily: '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu',
  botBubbleColor: '#9493D3',
  botFontColor: '#000',
  userBubbleColor: '#fff',
  userFontColor: '#2927A6',
};

function scrollToBottom() {
  // scroll main container for mobile
  const scrollOptions = {
    left: 0,
    top: document.body.scrollHeight,
    behavior: 'smooth'
  };

  window.scrollTo(scrollOptions);

  // scroll accordion container for desktop
  // const container = document.querySelector('.accordion-container');
  // if (container) {
  //   container.scrollTo({
  //     top: container.scrollHeight,
  //     behavior: 'smooth'
  //   });
  // }
}

function replacePlaceWithLink(text, data) {
  Object.keys(data).forEach(category => {
    data[category].forEach(item => {
      const place = item?.place;
      const url = item?.url;
      
      if (url) {
        const regex = new RegExp(place, 'gi');
        const anchor = `<a href="${url}" target="_blank" >${place}</a>`;
        text = text.replace(regex, anchor);
      }
    });
  });
  return text;
}

function getOrder(description, placesData) {
  const lowerCaseDescription = description.toLowerCase();
  const results = [];

  for (const category in placesData) {
    const relevantPlaces = placesData[category];
    relevantPlaces.forEach((place) => {
      const placeName = place.place.toLowerCase();
      const index = lowerCaseDescription.indexOf(placeName);
      if (index !== -1) {
        results.push({ place, index });
      }
    });
  }

  results.sort((a, b) => a.index - b.index);

  return results.map((result) => result.place);
};

const saveItinerary = async (itinerary) => {
  try {
    const response = await fetch(`${hostname}/api/save-itinerary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(itinerary),
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const result = await response.json();  

    debug && console.log('Itinerary saved with ID:', result.id);

    return result.id;
  } catch (err) {
    console.error('Error saving itinerary:', err);
  }
};

const fetchItinerary = async (id) => {
  try {
    const response = await fetch(`${hostname}/get-itinerary/${id}`);

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const result = await response.json();

    if (result.status === 'success') {
      console.log('Fetched itinerary:', result.itinerary);
      return result.itinerary;
    } else {
      console.log('Error fetching itinerary:', result.message);
    }
  } catch (err) {
    console.error('Error fetching itinerary:', err);
  }
};

async function asyncFetchData(url, data) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: data,
      headers: {
        'Content-Type': 'text/plain',
      },
    });
    const result = await response.json();
    return JSON.stringify(result);
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
}

// Custom component in order to be able to render links in messages
const ConsentMessage = ({ previousStep, triggerNextStep }) => {
  const messageContent = {
    __html:
      'Before I generate your custom itinerary, please let me know if you agree that by using the TripGPT application, you have read, understood, and agree to be bound by our <a href="https://docs.google.com/document/d/1oRc46RVs3vzNrb_IHjDW2jIzPeucqmUIU3FcCrBbMIg/edit" target="_blank" rel="noopener noreferrer">Disclaimer and Consent Form</a>',
  };

  return <div dangerouslySetInnerHTML={messageContent}></div>;
};

// Fixes the React warning
const ConsentMessageWrapper = (props) => {
  const filteredProps = Object.keys(props)
    .filter((key) => key !== "previousStep" && key !== "triggerNextStep")
    .reduce((obj, key) => {
      obj[key] = props[key];
      return obj;
    }, {});

  return <ConsentMessage {...filteredProps} />;
};


class TripGPT extends Component {
  constructor(props) {
    super(props);
    const urlParams = new URLSearchParams(window.location.search);
    const itineraryId = urlParams.get('itineraryId'); // this will be null if it's not in the URL
    this.state = {
      consented: true,
      isLoading: false,
      isSaveOpen: false,
      isMailingListOpen: false,
      totalDuration: 0,
      destinations: [],
      travelMethod: '',
      foodPreference: '',
      departureDate: '',
      tripType: '',
      budget: '',
      additionalInfo: '',
      dailyItineraryData: [],
      orderedDailyItineraryData: [],
      dailyItineraryDescriptions: [],
      dailyItineraryDescriptionsWithLinks: [],
      currDayIndex: 0,
      openItems: [true],
      activeCardIndex: 0,
      modifyDay: null,
      modifyDayInput: '',
      showLeftArrow: true,
      showRightArrow: true,
      itineraryId: itineraryId,
      email: null,
    };
    this.cardsContainerRef = React.createRef();
  }

  async componentDidMount() {
    const container = this.cardsContainerRef.current;
    container.addEventListener('scroll', this.handleScroll);

    const { itineraryId } = this.state;
    if (itineraryId) {
      const itineraryData = await fetchItinerary(itineraryId);
      if (itineraryData) {
        // loop through each day and add an openItems array
        const openItems = [];
        itineraryData.dailyItineraryData.forEach((day) => {
          openItems.push(true);
        });
        itineraryData.openItems = openItems;
        this.setState(itineraryData);
      }
    }
  }

  componentWillUnmount() {
    const container = this.cardsContainerRef.current;
    container.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    const container = this.cardsContainerRef.current;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
  
    this.setState({
      showLeftArrow: container.scrollLeft > 0,
      showRightArrow: container.scrollLeft < maxScrollLeft,
    });
  };

  handleChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  addItem = (index) => {
    this.setState((prevState) => {
      let newOpenItems = [
        ...prevState.openItems
      ];
      newOpenItems.push(true);
      return {openItems: newOpenItems };
    });
  };

  toggleItem = (index) => {
    this.setState((prevState) => {
      const newOpenItems = prevState.openItems.map((open, i) => {
        return i === index ? !open : open;
      });

      return { 
        openItems: newOpenItems,
      };
    });
  };

  handleCardClick = (index) => {
    this.toggleItem(index);
    // if the google maps places data is not loaded yet,
    // don't change the active card
    if (!this.state.orderedDailyItineraryData[index]) {
      return;
    }
    this.setState((prevState) => {
      return { 
        activeCardIndex: index
      };
    });
    this.scrollToDay(index);
  };

  // returns the city from state based on the specified day
  getCity = (day) => {
    let currentDay = 0;

    for (let i = 0; i < this.state.destinations.length; i++) {
        let destination = this.state.destinations[i];

        if (day >= currentDay && day <= currentDay + destination.duration - 1) {
            return destination.city;
        }

        currentDay += destination.duration;
    }

    return null; // Return null if the day is outside the range of the destinations
  }

  saveClicked = async () => {
    this.setState(() => {
      return {
        isSaveOpen: true,
      }
    })
  };

  mailingListClicked = async () => {
    this.setState(() => {
      return {
        isMailingListOpen: true,
      }
    })
  };

  handleSignupClose = async () => {
    this.setState(() => {
      return {
        isSaveOpen: false,
        isMailingListOpen: false,
      }
    })
  };
  

  handleSignup = async (email) => {
    try {
      const response = await fetch(`${hostname}/api/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, itineraryId: this.state.itineraryId })
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      await response.json();

      this.setState(() => {
        return {
          email: email,
        }
      });

      if ('URLSearchParams' in window) {
        var searchParams = new URLSearchParams(window.location.search)
        searchParams.set("itineraryId", this.state.itineraryId);
        var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
        window.history.pushState(null, '', newRelativePathQuery);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  handleMailingListSignup = async (email) => {
    try {
      const response = await fetch(`${hostname}/api/mailinglist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
  
      const data = await response.json();
  
      // Return data in a format that you expect in handleSignup function
      return { success: true, data };
  
    } catch (error) {
      console.error('Error:', error);
      // Return error in a format that you expect in handleSignup function
      return { success: false, error };
    }
  };
  
  
 

  processResponse = (dayIndex) => {
    return (reader) => {
      return new Promise((resolveProcess, rejectProcess) => {
        let currentDescription = '';
        let currentData = '';
        let isOnDesc = true;
        let currDayIndex = dayIndex || this.state.currDayIndex;

        this.setState(() => {
          return {
            isLoading: true,
          }
        });

        // Reset the state for the description with links incase this is for the "modify" case
        if (dayIndex !== undefined) {
          this.setState((prevState) => {
            let newDailyItineraryDescriptionsWithLinks = [
                ...prevState.dailyItineraryDescriptionsWithLinks
            ];
            newDailyItineraryDescriptionsWithLinks[currDayIndex] = null;
            
            return {
              dailyItineraryDescriptionsWithLinks: newDailyItineraryDescriptionsWithLinks
            }
          });
        }

        const processResult = (result) => {
          //if (result.done || currDayIndex >= this.state.totalDuration) {
          if (result.done) {
            debug && console.log('done LLM stream');
            // Do something when all of the data has been received
            setTimeout(() => {
              // if we are still in isLoading state here, that means
              // we didn't get enough days, making it such that the other
              // call to saveItinerary was never called, so we will save
              // the itinerary here
              if (this.state.isLoading === true) {
                resolveProcess();
              
              }
            }, 5000);
            return;
          }
          let newData = decoder.decode(result.value);
          let lines = newData.split('\n');
          let addedNewLine = false;
          
          lines.forEach((line) => {
            if (line.startsWith('{')) {
              currentData += line;
              isOnDesc = false;
            } else if (line.indexOf('{') !== -1) {
              // this means that GPT didn't do what we needed, which was to put the description on a separate line
              currentDescription += line.substring(0, line.indexOf("{"));
              currentData += line.substring(line.indexOf("{"));
              this.setState((prevState) => {
                let newDailyItineraryDescriptions = [
                    ...prevState.dailyItineraryDescriptions
                ];

                // prevent adding more days than specified
                if (currDayIndex < this.state.totalDuration) {
                  newDailyItineraryDescriptions[currDayIndex] = currentDescription;
                }

                return {
                  dailyItineraryDescriptions: newDailyItineraryDescriptions
                }
              });
              isOnDesc = false;
            } else {
              if (isOnDesc) {
                currentDescription += line;
                if (newData.indexOf('\n') !== -1 && !addedNewLine) {
                  console.log('newline');
                  currentDescription += '<br/><br/>';
                  addedNewLine = true;
                }
                this.setState((prevState) => {
                  let newDailyItineraryDescriptions = [
                      ...prevState.dailyItineraryDescriptions
                  ];
                  // prevent adding more days than specified
                  // TODO: IMPROVE THIS TO PREVENT ADDING MORE DAYS THAN SPECIFIED PER CITY
                  if (currDayIndex < this.state.totalDuration) {
                    newDailyItineraryDescriptions[currDayIndex] = currentDescription;
                  }

                  // setTimeout(() => {
                  //   scrollToBottom();
                  // }, 250);
                  
                  return {
                    dailyItineraryDescriptions: newDailyItineraryDescriptions
                  }
                });
              } else {
                currentData += line;
              }
              

              if (line.endsWith('}')) {
                isOnDesc = true;

                debug && console.log('currentData', currentData);
                
                this.setState((prevState) => {
                  let newDailyItineraryDescriptions = [
                    ...prevState.dailyItineraryDescriptions
                  ];
                  let newDailyItineraryData = [
                    ...prevState.dailyItineraryData
                  ];
                  let newOrderedDailyItineraryData = [
                    ...prevState.orderedDailyItineraryData
                  ];
                  newDailyItineraryDescriptions[currDayIndex] = currentDescription;
                  // we push an empty object for now, and we will replace it once our fetch
                  // to get-places is done
                  newDailyItineraryData.push({});

                  const getPlaces = (currDayIndex, currentData) => {
                    let placesData = {
                      'city': this.getCity(currDayIndex),
                      'data': JSON.parse(currentData)
                    }
                    let placesDataString = JSON.stringify(placesData);
                    fetch(`${hostname}/api/get-places`, {
                        method: 'POST',
                        body: placesDataString,
                        headers: {
                          'Content-Type': 'application/json'
                        }
                      })
                      .then(res => res.json())
                      .then(data => {
                        // get the response and set it as currDayIndex
                        this.setState((prevState) => {
                          let newDailyItineraryData = [
                            ...prevState.dailyItineraryData
                          ];
                          let newDailyItineraryDescriptions = [
                            ...prevState.dailyItineraryDescriptions
                          ];
                          let newDailyItineraryDescriptionsWithLinks = [
                            ...prevState.dailyItineraryDescriptionsWithLinks
                          ];
                          // only replace the descriptions with links if the json is valid
                          newDailyItineraryDescriptionsWithLinks[currDayIndex] = replacePlaceWithLink(newDailyItineraryDescriptions[currDayIndex], data);
                          newDailyItineraryData[currDayIndex] = data;
                          newOrderedDailyItineraryData[currDayIndex] = getOrder(newDailyItineraryDescriptions[currDayIndex], data);

                          // setTimeout(() => {
                          //   this.scrollToDay(currDayIndex);
                          // }, 500);
                          
                          if (currDayIndex + 1 >= prevState.totalDuration) {
                            resolveProcess();
                          }

                          return {
                            dailyItineraryData: newDailyItineraryData,
                            dailyItineraryDescriptionsWithLinks: newDailyItineraryDescriptionsWithLinks,
                            orderedDailyItineraryData: newOrderedDailyItineraryData,
                          }
                        });
                      });
                  }

                  // currentData += '}}'; // TODO: remove this hack, just needed for testing
                  // check if currentData is valid json, and if it is, get places,
                  // it's not, fix the json and get places
                  try {
                    JSON.parse(currentData);
                    getPlaces(currDayIndex, currentData);
                  } catch (e) {
                    (function(currDayIndex, currentData){
                      console.error(`invalid json for day ${currDayIndex} with data ${currentData}, fixing...`);
                      asyncFetchData(`${hostname}/api/fix-json`, currentData).then((fixedData) => {
                        try {
                          getPlaces(currDayIndex, fixedData);
                        } catch (e) {
                          console.error(e);
                        }
                      });
                    })(currDayIndex, currentData);
                  }

                  // increment the day
                  currDayIndex++;
                  this.addItem(currDayIndex);
                  // let prevDay = currDayIndex - 1;
                  // if (prevDay != 0) {
                  //   this.toggleItem(prevDay);
                  // }
                  
                  return {
                    dailyItineraryDescriptions: newDailyItineraryDescriptions,
                    dailyItineraryData: newDailyItineraryData,
                    currDayIndex: currDayIndex
                  };
                });
                
                
                
                currentData = '';
                currentDescription = '';
              }
            }
          });
          // Do something with the data as it streams in
          reader.read().then(processResult).catch(rejectProcess);
        };
        
        reader.read().then(processResult).catch(rejectProcess);
      });
    };
  }

  handleModifyEnd = async () => {
    const { destinations, 
      foodPreference,
      departureDate,
      tripType,
      budget,
      additionalInfo,
      modifyDay,
      modifyDayInput,
      dailyItineraryDescriptions } = this.state;

      const destination = destinations[modifyDay-1].city;

      // create a string that takes the dailyItineraryDescriptions, maps over them, starts them with "Day <id>:" and then joins them with \n"
      let dailyItinerary = dailyItineraryDescriptions.map((description, index) => {
        return "Day " + (index + 1) + ": " + description;
      }).join("\n");

      let res = await fetch(`${hostname}/api/plan-trip/modify/${modifyDay}`, {
        method: 'POST',
        body: JSON.stringify({ destinations, destination, foodPreference, departureDate, tripType, budget, additionalInfo, modifyDay, modifyDayInput, dailyItinerary }),
        headers: {
          'Content-Type': 'application/json'
        }
      })
      // Process the response
      const reader = await res.body.getReader();
      this.processResponse(modifyDay - 1)(reader);
        // .then(() => {
        //   this.setState({
        //     modifyDay: null,
        //     email: null, // temp hack to allow for saving again
        //   });
        // }); 
    
  };

  handleIntroEnd = async () => {
    const { destinations, foodPreference, departureDate, tripType, budget, additionalInfo, consented, dailyItineraryData } = this.state;
  
    if (consented === false) {
      return;
    }
  
    if (window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }
  
    try {
      // add up the durations of the destinations and store it in totalDuration state
      let totalDuration = 0;
      destinations.forEach(destination => {
        totalDuration += destination.duration;
      });
      this.setState({ totalDuration });
  
      // We will use Array.reduce to handle the promises sequentially

      const result = await destinations.reduce(async (previousPromise, destination, index) => {
        await previousPromise; // this will ensure that each fetch waits for the previous one to complete
        totalDuration += destination.duration;
        console.log('fetching for destination', index);
        // Fetch for each destination
        const res = await fetch(`${hostname}/api/plan-trip`, {
          method: 'POST',
          body: JSON.stringify({ 
            destination: destination, 
            destinationIndex: index,
            destinations, 
            foodPreference, 
            departureDate, 
            tripType, 
            budget, 
            additionalInfo, 
            dailyItineraryData,
          }),
          headers: {
            'Content-Type': 'application/json'
          }
        });
      
        // Process the response
        const reader = await res.body.getReader();
        return this.processResponse()(reader);
      }, Promise.resolve());

      this.setState((prevState) => {
        return {
          isLoading: false,
        }
      });

      saveItinerary({
        totalDuration: this.state.totalDuration,
        destinations: this.state.destinations,
        travelMethod: this.state.travelMethod,
        foodPreference: this.state.foodPreference,
        departureDate: this.state.departureDate,
        tripType: this.state.tripType,
        budget: this.state.budget,
        additionalInfo: this.state.additionalInfo,
        dailyItineraryData: this.state.dailyItineraryData,
        dailyItineraryDescriptions: this.state.dailyItineraryDescriptions,
        dailyItineraryDescriptionsWithLinks: this.state.dailyItineraryDescriptionsWithLinks,
        orderedDailyItineraryData: this.state.orderedDailyItineraryData,
        verson: "1.0.0",
      }).then((itineraryId) => {
        if (itineraryId === null) {
          console.error('itineraryId is null, this was unexpected');
        }
        this.setState((prevState) => {
          return {
            itineraryId: itineraryId,
          }
        });
      });
  
      // Save the total duration once all fetches have completed
      this.setState({ totalDuration });
    } catch (err) {
      console.error(err);
    }
  };
  
  

  addDestinationDuration = destinationDuration => {
    this.setState(prevState => {
      
      prevState.destinations[prevState.destinations.length-1].duration = destinationDuration
      return {
        destinations: [...prevState.destinations]      }
    });
  }

  addDestination = newDestination => {
    this.setState(prevState => ({
      destinations: prevState.destinations.concat({city: newDestination})
    }));
  }

  addAdditionalInfo = additionalInfo => {
    this.setState(prevState => ({
      additionalInfo: additionalInfo
    }));
  }

  foodPreferenceTrigger = ({value}) => {
    this.setState({foodPreference: value})
    return 'tripType';
  }

  // travelMethodTrigger = ({value}) => {
  //   this.setState({travelMethod: value})
  //   return 'departureDate';
  // }

  tripTypeTrigger = ({value}) => {
    this.setState({tripType: value})
    return 'budget';
  }

  budgetTrigger = ({value}) => {
    this.setState({budget: value})
    return 'additionalInfo';
  }

  declineTrigger = () => {
    this.setState({consented: false})
    return 'declineEnd';
  }

  isValidDate = (dateString) => {
    // First check for the pattern
    if(!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString))
        return false;
  
    // Parse the date parts to integers
    var parts = dateString.split("/");
    var day = parseInt(parts[1], 10);
    var month = parseInt(parts[0], 10);
    var year = parseInt(parts[2], 10);
  
    // Check the ranges of month and year
    if(year < 2023 || year > 3000 || month == 0 || month > 12)
        return false;
  
    var monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];
  
    // Adjust for leap years
    if(year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
        monthLength[1] = 29;
  
    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
  }

  shouldDisplayDayLabel = (placeIndex, keyIndex) => {
    return placeIndex === 0 && keyIndex === 0;
  };

  handleSelect = (activeKey) => {
    this.setState({
      activeCardIndex: activeKey
    })
  }

  scrollToDay = (dayIndex) => {
    const dayLabels = document.querySelectorAll('.day-label');
    const targetEl = dayLabels[dayIndex]?.parentElement?.parentElement;
  
    if (!targetEl) {
      return
    }

    let prevElement = targetEl.previousElementSibling;
    let scrollPosition = 0;
    while (prevElement) {
      scrollPosition += prevElement.offsetWidth;
      prevElement = prevElement.previousElementSibling;
    }

    // Scroll the container to the calculated position
    const cardsContainer = targetEl.parentElement.parentElement;
    cardsContainer.scroll({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  scrollLeft = () => {
    const container = this.cardsContainerRef.current;
    container.scroll({
      left: container.scrollLeft - 200,
      behavior: "smooth",
    });
  };
  
  scrollRight = () => {
    const container = this.cardsContainerRef.current;
    container.scroll({
      left: container.scrollLeft + 200,
      behavior: "smooth",
    });
  };

  CustomSubmitButton = ({ sendMessage }) => {
    return (
      <button
        onClick={sendMessage}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1.5rem',
        }}
      >
        ✈
      </button>
    );
  };

  modifyDay = (day) => {
    this.setState({
      modifyDay: day
    })
  }

  modifySteps = () => {

    let stepArray = [
      {
        id: '1',
        message: `What would you like to modify about Day ${this.state.modifyDay}? For example, you can tell me what activity you'd like to do, or what specific restaurant you'd like to eat at`,
        trigger: '2',
      },
      {
        id: '2',
        user: true,
        validator: (value) => {
          this.setState({
            modifyDayInput: value
          });
          return true;
        },
        end: true,
      },
    ];

    return stepArray;
  };

  introSteps = () => {

    let stepArray = [
      {
        id: 'dev',
        message: 'Are you testing and want to skip and just generate an itinerary using the sample data?',
        trigger: 'devInput',
      },
      {
        id: 'devInput',
        options: [
          { value: 1, label: 'Yes', trigger: () => {
            this.setState({
              destinations: [
                {city: "Vancouver", duration: 2},
                {city: "Whistler", duration: 1},
                // {city: "Kings Canyon National Park", duration: 1},
                // {city: "Sequoia National Park", duration: 2},
                // {city: "Pismo Beach", duration: 1},
                // {city: "Malibu", duration: 1},
                // {city: "Los Angeles", duration: 1},
                // {city: "Joshua Tree National Park", duration: 1},
                // {city: "Palm Springs", duration: 1},
                // {city: "San Diego", duration: 2},
              ],
              //travelMethod: 'Car',
              foodPreference: 'None',
              departureDate: '06/28/2023',
              tripType: 'road trip',
              budget: 'mid-range',
              additionalInfo: 'I want to see national parks, I love outdoors adventures',
            })
            return 'generateItinerary';
          } },
          { value: 2, label: 'No', trigger: 'welcome' },
        ],
      },
      {
        id: 'welcome',
        message: 'Welcome traveler, to the future of trip planning! ',
        trigger: 'destination'
      },
      {
        id: 'destination',
        message: 'What city are you planning on traveling to?',
        trigger: 'destinationInput',
      },
      {
        id: 'destinationInput',
        user: true,
        validator: (value) => {
          if (!value) {
            return 'Enter a destination';
          }
          this.addDestination(value)
          return true;
        },
        trigger: 'duration'
      },
      {
        id: 'duration',
        message: 'How many days are you planning to go to {previousValue} for?',
        trigger: 'durationInput',
      },
      {
        id: 'durationInput',
        user: true,
        validator: (value) => {
          if (isNaN(value) || value < 1) {
            return 'Enter valid number';
          }
          if (value > 21) {
            return 'For now, you can not plan a trip longer than 21 days';
          }
          this.addDestinationDuration(parseInt(value));
          return true;
        },
        trigger: 'multipleDestinations'
      },
      {
        id: 'multipleDestinations',
        message: 'Will you be visiting more cities on this trip?',
        trigger: 'multipleDestinationsOptions'
      },
      {
        id: 'multipleDestinationsOptions',
        options: [
          { value: 1, label: 'No', trigger: 'foodPreference' },
          { value: 2, label: 'Yes', trigger: 'additionalDestination' },
        ],
      },
      {
        id: 'additionalDestination',
        message: 'What city do you plan on visiting next?',
        trigger: 'destinationInput',
      },
      // {
      //   id: 'travelMethod',
      //   message: 'How do you plan on traveling between cities?',
      //   trigger: 'travelMethodOptions',
      // },
      {
        id: 'foodPreference',
        message: 'Let’s talk about your food preferences. Do you have any dietary restrictions? We will try to find restaurants that fit your preferences.',
        trigger: 'foodPreferenceOptions',
      },
      {
        id: 'foodPreferenceOptions',
        options: [
          { value: 'no', label: 'No', trigger: this.foodPreferenceTrigger },
          { value: 'vegetarian-friendly', label: 'Vegetarian-friendly', trigger: this.foodPreferenceTrigger },
          { value: 'vegan-friendly', label: 'Vegan-friendly', trigger: this.foodPreferenceTrigger },
          { value: 'paleo-friendly', label: 'Paleo-friendly', trigger: this.foodPreferenceTrigger },
        ],
      },
      // {
      //   id: 'travelMethodOptions',
      //   options: [
      //     { value: 'car', label: 'Car', trigger: this.travelMethodTrigger },
      //     { value: 'plane', label: 'Plane', trigger: this.travelMethodTrigger },
      //     { value: 'train', label: 'Train', trigger: this.travelMethodTrigger },
      //   ],
      // },
      // {
      //   id: 'departureDate',
      //   message: 'When do you plan on departing? Please share date in the following format: mm/dd/yyyy',
      //   trigger: 'departureDateInput',
      // },
      // {
      //   id: 'departureDateInput',
      //   user: true,
      //   validator: (value) => {
      //     if (!this.isValidDate(value)) {
      //       return 'Enter a valid date using mm/dd/yyyy format';
      //     }
      //     this.setState({departureDate: value})
          
      //     return true;
      //   },
      //   trigger: 'tripType'
      // },
      {
        id: 'tripType',
        message: 'What type of trip are you looking for?',
        trigger: 'tripTypeInput',
      },
      {
        id: 'tripTypeInput',
        options: [
          { value: 'off the beaten path', label: 'Off the beaten path', trigger: this.tripTypeTrigger },
          { value: 'adventurous', label: 'Adventurous', trigger: this.tripTypeTrigger },
          { value: 'chill', label: 'Chill', trigger: this.tripTypeTrigger },
          { value: 'touristy', label: 'Touristy', trigger: this.tripTypeTrigger },
          { value: 'beach', label: 'Beach', trigger: this.tripTypeTrigger },
          { value: 'road trip', label: 'Road trip', trigger: this.tripTypeTrigger },
          { value: 'romantic', label: 'Romantic', trigger: this.tripTypeTrigger },
          { value: 'culinary', label: 'Culinary', trigger: this.tripTypeTrigger },
          { value: 'nature', label: 'Nature', trigger: this.tripTypeTrigger },
          { value: 'a mix', label: 'A mix', trigger: this.tripTypeTrigger },
        ],
      },
      {
        id: 'budget',
        message: 'What type of budget are you on for this trip?',
        trigger: 'budgetInput',
      },
      {
        id: 'budgetInput',
        options: [
          { value: 'backpacker', label: 'Backpacker', trigger: this.budgetTrigger },
          { value: 'midrange', label: 'Mid-range', trigger: this.budgetTrigger },
          { value: 'luxury', label: 'Luxury', trigger: this.budgetTrigger },
        ],
      },
      {
        id: 'additionalInfo',
        message: 'What else do you want me to know about what you want to do on this trip? (i.e. go to a museum, go to a concert, etc.). I want to make sure this trip is as perfect for you as possible!',
        trigger: 'additionalInfoInput',
      },
      {
        id: 'additionalInfoInput',
        user: true,
        validator: (value) => {
          this.addAdditionalInfo(value)
          return true;
        },
        trigger: 'consent'
      },
      {
        id: 'consent',
        component: <ConsentMessageWrapper/>,
        asMessage: true,
        trigger: 'consentInput',
      },
      {
        id: 'consentInput',
        options: [
          { value: 'accept', label: 'I agree', trigger: 'generateItinerary' },
          { value: 'decline', label: 'I don\'t agree', trigger: this.declineTrigger },
        ],
      },
      {
        id: 'declineEnd',
        message: 'It seems you have not agreed to our Disclaimer and Consent Form, and therefore I can’t generate a custom itinerary for you, but I wish you safe and pleasant travels! Bye!',
        end: true,
      },
      {
        id: 'generateItinerary',
        message: 'Thanks for chatting! Please hang tight, I’m generating your custom itinerary...',
        end: true,
      },
    ]

    const searchParams = new URLSearchParams(window.location.search);
    if (window.location.hostname !== 'localhost' && searchParams.get('debug') !== 'true') {
      // remove first two steps if on localhost
      stepArray.shift();
      stepArray.shift();
    }

    return stepArray;
  };

  render() {
    const { dailyItineraryDescriptions, dailyItineraryData, orderedDailyItineraryData, modifyDay, activeCardIndex, itineraryId, isLoading, isSaveOpen, isMailingListOpen, email } = this.state;

    const shouldRenderMap = orderedDailyItineraryData.length > 0 && orderedDailyItineraryData[activeCardIndex].length > 0 && modifyDay === null;
    const isLoadingItinerary = dailyItineraryData.length === 0 && itineraryId !== null;
    
    const urlParams = new URLSearchParams(window.location.search);
    const currentItineraryMatchesUrlItinerary = urlParams.get('itineraryId') === itineraryId;
    const isSaved = email !== null || currentItineraryMatchesUrlItinerary;
    
    return (
      <div className="maincontainer">
        <header className="header">
          <div className="logo">
            <a href="/" style={{ textDecoration: 'none', color: 'black' }}>
              TripGPT_
            </a>
          </div>
          <div className="beta-label">
            BETA
          </div>
          <button onClick={this.mailingListClicked} className="mailinglist-button button-animation">
              Join the mailing list!
          </button>
        </header>
        <div className="top-container">
          {shouldRenderMap ? (
            <TravelPlanMap apiKey="AIzaSyCYMr8GTGArsV0A53-5Pm47wJuuzbRDsAI" places={orderedDailyItineraryData[activeCardIndex]} /> 
          ) : (
          isLoadingItinerary ? (
            <div className="loading-spinner">
              <Spinner animation="border" size="sm" />
            </div>
            ) : (
            <div className="chatbot">
              <ThemeProvider theme={theme}>
                {modifyDay === null ? (
                  <div className="intro-chatbot">
                    <ChatBot
                      key={-1}
                      handleEnd={() => {
                        this.handleIntroEnd();
                      }}
                      steps={this.introSteps()}
                      botAvatar={null}
                      placeholder=""
                      customSubmitButton={(sendMessage) => (
                        <button
                          onClick={sendMessage}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                          }}
                        >
                          ✈
                        </button>
                      )}
                    />
                  </div>
                ) : (
                  <div className="modify-chatbot">
                    <ChatBot
                      key={modifyDay}
                      handleEnd={() => {
                        this.handleModifyEnd();
                      }}
                      steps={this.modifySteps()}
                      botAvatar={null}
                      placeholder=""
                      customSubmitButton={(sendMessage) => (
                        <button
                          onClick={sendMessage}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                          }}
                        >
                          ✈
                        </button>
                      )}
                    />
                  </div>
                )}
              </ThemeProvider>
            </div>
            )
          )}

          {dailyItineraryDescriptions.length > 0 &&
          <div className="accordion-section">
            {isSaved && !isLoading ? (
              <div className="save-button-container">
              <button className={`save-button ${isLoading ? 'loading' : ''}`} onClick={this.saveClicked} disabled={isLoading}>
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 13C16.4092 13.0039 15.8266 13.1386 15.294 13.3945C14.7615 13.6504 14.2922 14.0211 13.92 14.48L8.82003 12.13C9.05982 11.3957 9.05982 10.6042 8.82003 9.86999L13.92 7.51999C14.5216 8.24596 15.3604 8.73607 16.2882 8.90383C17.216 9.07159 18.1732 8.90622 18.991 8.4369C19.8087 7.96757 20.4344 7.22447 20.7576 6.33874C21.0808 5.453 21.0807 4.48157 20.7573 3.5959C20.434 2.71022 19.8082 1.96724 18.9903 1.49806C18.1725 1.02889 17.2152 0.863686 16.2875 1.03162C15.3597 1.19955 14.521 1.68981 13.9196 2.41589C13.3181 3.14196 12.9924 4.05717 13 4.99999C13.003 5.23822 13.0265 5.47576 13.07 5.70999L7.79003 8.13999C7.22711 7.58957 6.51427 7.21751 5.74083 7.07042C4.96739 6.92333 4.16775 7.00776 3.44208 7.31312C2.71641 7.61849 2.09699 8.13121 1.66143 8.78705C1.22587 9.4429 0.99353 10.2127 0.99353 11C0.99353 11.7873 1.22587 12.5571 1.66143 13.2129C2.09699 13.8688 2.71641 14.3815 3.44208 14.6869C4.16775 14.9922 4.96739 15.0767 5.74083 14.9296C6.51427 14.7825 7.22711 14.4104 7.79003 13.86L13.07 16.29C13.0265 16.5242 13.003 16.7618 13 17C13 17.7911 13.2346 18.5645 13.6742 19.2223C14.1137 19.8801 14.7384 20.3928 15.4693 20.6955C16.2002 20.9983 17.0045 21.0775 17.7804 20.9231C18.5563 20.7688 19.269 20.3878 19.8285 19.8284C20.3879 19.269 20.7688 18.5563 20.9232 17.7804C21.0775 17.0044 20.9983 16.2002 20.6955 15.4693C20.3928 14.7384 19.8801 14.1136 19.2223 13.6741C18.5645 13.2346 17.7912 13 17 13ZM17 2.99999C17.3956 2.99999 17.7823 3.11729 18.1112 3.33705C18.4401 3.55682 18.6964 3.86917 18.8478 4.23463C18.9992 4.60008 19.0388 5.00221 18.9616 5.39017C18.8844 5.77813 18.6939 6.1345 18.4142 6.41421C18.1345 6.69391 17.7782 6.88439 17.3902 6.96156C17.0022 7.03873 16.6001 6.99913 16.2347 6.84775C15.8692 6.69638 15.5569 6.44003 15.3371 6.11113C15.1173 5.78223 15 5.39556 15 4.99999C15 4.46956 15.2107 3.96085 15.5858 3.58578C15.9609 3.21071 16.4696 2.99999 17 2.99999ZM5.00003 13C4.60447 13 4.21779 12.8827 3.88889 12.6629C3.55999 12.4432 3.30364 12.1308 3.15227 11.7654C3.00089 11.3999 2.96129 10.9978 3.03846 10.6098C3.11563 10.2218 3.30611 9.86548 3.58581 9.58578C3.86552 9.30607 4.22189 9.11559 4.60985 9.03842C4.99781 8.96125 5.39994 9.00086 5.7654 9.15223C6.13085 9.30361 6.4432 9.55995 6.66297 9.88885C6.88273 10.2178 7.00003 10.6044 7.00003 11C7.00003 11.5304 6.78931 12.0391 6.41424 12.4142C6.03917 12.7893 5.53046 13 5.00003 13ZM17 19C16.6045 19 16.2178 18.8827 15.8889 18.6629C15.56 18.4432 15.3036 18.1308 15.1523 17.7654C15.0009 17.3999 14.9613 16.9978 15.0385 16.6098C15.1156 16.2218 15.3061 15.8655 15.5858 15.5858C15.8655 15.3061 16.2219 15.1156 16.6098 15.0384C16.9978 14.9613 17.3999 15.0009 17.7654 15.1522C18.1308 15.3036 18.4432 15.56 18.663 15.8889C18.8827 16.2177 19 16.6044 19 17C19 17.5304 18.7893 18.0391 18.4142 18.4142C18.0392 18.7893 17.5305 19 17 19Z" fill="black"/>
                </svg>
                <span>Share Itinerary</span>
              </button>
            </div>
            ) : (
              <div className="save-button-container">
                <button className={`save-button ${isLoading ? 'loading' : ''}`} onClick={this.saveClicked} disabled={isLoading}>
                  <svg width="14" height="20" viewBox="0 0 14 20" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="save-icon">
                    <path d="M13 0H1C0.734784 0 0.48043 0.105357 0.292893 0.292893C0.105357 0.48043 2.16531e-09 0.734784 2.16531e-09 1V19C-1.25753e-05 19.1912 0.0547687 19.3783 0.157854 19.5393C0.260939 19.7003 0.408009 19.8284 0.58164 19.9083C0.755271 19.9883 0.948188 20.0169 1.13754 19.9906C1.32688 19.9643 1.50473 19.8843 1.65 19.76L7 15.27L12.29 19.71C12.3834 19.8027 12.4943 19.876 12.6161 19.9258C12.7379 19.9755 12.8684 20.0008 13 20C13.1312 20.0034 13.2613 19.976 13.38 19.92C13.5626 19.845 13.7189 19.7176 13.8293 19.5539C13.9396 19.3901 13.999 19.1974 14 19V1C14 0.734784 13.8946 0.48043 13.7071 0.292893C13.5196 0.105357 13.2652 0 13 0V0ZM12 16.86L7.64 13.2C7.46031 13.0503 7.23385 12.9684 7 12.9684C6.76615 12.9684 6.53969 13.0503 6.36 13.2L2 16.86V2H12V16.86Z"/>
                  </svg>
                  <span>Save Itinerary</span>
                </button>
              </div>
            )}
            
            <Accordion className="accordion-container">
              
              {dailyItineraryDescriptions.map((description, index) => {
                return (
                  <Card key={index}>
                    <Card.Header onClick={() => this.handleCardClick(index)}>
                      Day {index + 1} ({this.getCity(index)})
                      <svg
                        className="arrow-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                      >
                        <path
                          d="M12 8l-6 6 1.414 1.414L12 10.828l4.586 4.586L18 14z" 
                          transform={this.state.openItems[index] ? "" : "rotate(180, 12, 12)"}
                        />
                      </svg>
                    </Card.Header>
                    <Collapse
                      in={this.state.openItems[index]}
                      className="no-transition"
                    >
                      <Card.Body>
                        <div onClick={() => this.modifyDay(index+1)} className="day-modify" day={index}>
                          <a href="#">
                            <img src="/modify.svg" alt="Modify" /><span>Modify Day</span>
                          </a>
                          </div>
                          {!!this.state.dailyItineraryDescriptionsWithLinks[index] ? (
                            <div className="day-description" dangerouslySetInnerHTML={{ __html: this.state.dailyItineraryDescriptionsWithLinks[index] }} />
                          ) : (
                            <div className="day-description" dangerouslySetInnerHTML={{ __html: description}  } />
                          )}
                      </Card.Body>
                    </Collapse>
                  </Card>
                );
              })}
              {this.state.isLoading &&
              <div className="loading-spinner">
                <Spinner animation="border" size="sm" />
              </div>
              }
            </Accordion>
          </div>
          }
        </div>
        <div className="outer-container">
          {dailyItineraryData.length > 0 && this.state.showLeftArrow && (
            <div className="arrow left-arrow" onClick={this.scrollLeft}>
              <img src="/arrow-left.svg" alt="Arrow left" />
            </div>
          )}

          <div className="scroll-wrapper" ref={this.cardsContainerRef}>
            <div className="cards-container" ref={this.cardsContainerRef}>
            {dailyItineraryData.map((day, dayIndex) =>
              day &&
                Object.entries(day).flatMap(([key, places], keyIndex) =>
                  places.map((place, placeIndex) => {
                    
                    const dayLabelVisible = this.shouldDisplayDayLabel(placeIndex, keyIndex);
                    return (
                      place && (
                        <div key={`${dayIndex}-${keyIndex}-${placeIndex}`}
                          className="card-wrapper">
                          <div className="day-label-container">
                            {dayLabelVisible && (
                              <div className="day-label">Day {dayIndex + 1} ({this.getCity(dayIndex)})</div>
                            )}
                          </div>
                          <a
                            href={place?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="card-link"
                          >
                            <Card
                              className="card-container button-animation"
                              key={`${dayIndex}-${keyIndex}-${placeIndex}`}
                            >
                              <div className="image-container">
                                <Image src={place.photo} alt={place.place} className="full-image" />
                              </div>
                              <div className="info-container">
                                <div className="card-title" title={place.place}>{place.place}</div>
                                {place.rating !== undefined && (
                                  <div className="rating-container">
                                    <img src="/star.svg" alt="Star Icon" />
                                    <span>
                                      {place.rating}({place.user_ratings_total})
                                    </span>
                                  </div>
                                )}
                              </div>
                            </Card>
                          </a>
                        </div>
                      )
                    );
                  })
                )
            )}
            </div>
          </div>
          {dailyItineraryData.length > 0 && this.state.showRightArrow && (
            <div className="arrow right-arrow" onClick={this.scrollRight}>
              <img src="/arrow-right.svg" alt="Arrow right" />
            </div>
          )}
          
        </div>
        {/* Checking for isLoadingItinerary twice instead of wrapping both divs in one conditional
        because I'd need to fix the CSS for the sample itineraries and I'm too lazy right now! */}
        {!isLoadingItinerary && dailyItineraryDescriptions.length === 0 && (
        <div className="sample-itinerary-header">
            <div className="day-label">Try a sample itinerary:</div>
        </div>
        )}
        {!isLoadingItinerary && dailyItineraryDescriptions.length === 0 && (
          <SampleItineraries/>
        )}
        <ShareModal show={isSaveOpen} itineraryId={itineraryId} isSaved={isSaved} handleSignup={this.handleSignup} handleClose={this.handleSignupClose}/>
        <MailingListModal show={isMailingListOpen} handleSignup={this.handleMailingListSignup} handleClose={this.handleSignupClose}/>
        <Analytics/>
      </div>
  );
}
}

export default TripGPT;
